import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { DatePipe } from '../../../pipes/date.pipe';
import { ChartsConfigService } from '../../../components/charts/service/charts-config.service';
import { CurrencyPipe } from '../../../pipes/currency.pipe';
import { NumberPipe } from '../../../pipes/number.pipe';

import { BalanceTrendChartExtraConfig, ChartYAxisUnit, DataPoint, DataPoints } from './balances-summary.domain';
import type Highcharts from 'highcharts/highstock';

@Injectable({
  providedIn: 'root'
})
export class BalanceTrendChartService {
  private readonly chartsConfigService = inject(ChartsConfigService);
  private readonly datePipe = inject(DatePipe);
  private readonly numberPipe = inject(NumberPipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly translateService = inject(TranslateService);

  private static readonly DEFAULT_SERIES_LINE_COLOUR: string = '#008a00';
  private static readonly SECONDARY_SERIES_LINE_COLOUR: string = '#008a00';
  private static readonly DATE_PROPERTY_NAME: string = 'date';
  private static readonly VALUE_PROPERTY_NAME: string = 'equity';
  private static readonly X_AXIS_VERTICAL_TRAIL_CONFIG: Record<string, unknown> = {
    crosshair: {
      dashStyle: 'Dash',
      color: '#1c1c1c',
      zIndex: 3
    }
  };

  private static readonly Y_AXIS_FULL_WIDTH_CONFIG: Record<string, unknown> = {
    offset: 0,
    labels: {
      align: 'right',
      x: -20,
      y: -25,
      useHTML: true,
      style: {
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        borderRadius: '12.5px',
        padding: '4px 8px',
        whiteSpace: 'nowrap'
      }
    }
  };

  private static readonly CHART_FULL_WIDTH_CONFIG: Record<string, unknown> = {
    marginRight: -3,
    marginLeft: -3,
    spacingTop: 30
  };

  private static readonly SECONDARY_LINEAR_GRADIENT: Record<string, unknown> = {
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
      [0.6, 'rgba(242,249,242,1)'],
      [1, 'rgba(255,255,255,0)']
    ]
  };

  getChartConfiguration(
    seriesData: DataPoints,
    toolTipEnabled?: boolean,
    extraConfig: Partial<BalanceTrendChartExtraConfig> = {}
  ): {
      config: Highcharts.Options;
      dateRange: { startDate: Date; endDate: Date };
    } {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this;
    const tooltipObject: Highcharts.TooltipOptions = {
      useHTML: true,
      animation: false,
      backgroundColor: 'rgba(255,255,255,1)',
      formatter(): string {
        return (
          service.renderDate(this.x as unknown as Date) +
          '<br>' +
          service.shortenWithMagnitude(this.y)
        );
      }
    };
    const series = extraConfig.useSecondaryLinearGradient ?
      {
        name: '',
        type: 'area',
        data: this.chartsConfigService.mapArrayOfObjectsToHighchartsFormat(
          seriesData as unknown as Array<Record<string, unknown>>,
          BalanceTrendChartService.DATE_PROPERTY_NAME,
          BalanceTrendChartService.VALUE_PROPERTY_NAME
        ),
        fillColor: BalanceTrendChartService.SECONDARY_LINEAR_GRADIENT,
        lineWidth: 2,
        states: {
          hover: {
            lineWidthPlus: 0
          }
        },
        dataGrouping: {
          enabled: false
        },
        marker: {
          enabled: false
        }
      }
      :
      {
        name: '',
        type: 'area',
        data: this.chartsConfigService.mapArrayOfObjectsToHighchartsFormat(
          seriesData as unknown as Array<Record<string, unknown>>,
          BalanceTrendChartService.DATE_PROPERTY_NAME,
          BalanceTrendChartService.VALUE_PROPERTY_NAME
        ),
        fillColor: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0.6, 'rgba(242,249,242,1)'],
            [1, 'rgba(255,255,255,0)']
          ]
        },
        lineWidth: 2,
        dataGrouping: {
          enabled: false
        },
        marker: {
          enabled: false
        }
      };

    const balance = this.getBalanceMinAndMax(seriesData);
    const dateRange = this.getDateRange(seriesData);

    balance.max = this.ceilNumber(balance.max);
    balance.min = this.floorNumber(balance.min);

    const xAxisExtraConfig = extraConfig.hasVerticalTrail ? BalanceTrendChartService.X_AXIS_VERTICAL_TRAIL_CONFIG : {};
    const yAxisExtraConfig = extraConfig.isFullWidth ? BalanceTrendChartService.Y_AXIS_FULL_WIDTH_CONFIG : {};
    const chartExtraConfig = extraConfig.isFullWidth ? BalanceTrendChartService.CHART_FULL_WIDTH_CONFIG : {};
    const seriesLineColor = extraConfig.useSecondarySeriesLineColor ?
      BalanceTrendChartService.SECONDARY_SERIES_LINE_COLOUR : BalanceTrendChartService.DEFAULT_SERIES_LINE_COLOUR;
    if (extraConfig.hideYAxisGridLine) {
      yAxisExtraConfig.gridLineWidth = 0;
    }

    const config = this.chartsConfigService
      .createStockChartConfig()
      .addSeries(series)
      .addColor(seriesLineColor)
      .setProperty({
        propertyName: 'scrollbar.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'navigator.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'rangeSelector.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'tooltip',
        propertyValue: tooltipObject,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'tooltip.enabled',
        propertyValue: toolTipEnabled,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'plotOptions.series.states.hover.enabled',
        propertyValue: toolTipEnabled || extraConfig.hoverToShowSeriesMarker,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'plotOptions.series.point.events.mouseOver',
        propertyValue: extraConfig.pointMouseOver,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'plotOptions.series.point.events.mouseOut',
        propertyValue: extraConfig.pointMouseOut,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'title',
        propertyValue: {
          text: undefined
        },
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'legend',
        propertyValue: {
          enabled: false
        },
        defaultValue: {}
      })
      .extendXAxisProperties({
        lineColor: 'transparent',
        tickLength: 0,
        visible: false,
        ...xAxisExtraConfig
      })
      .setYAxis({
        offset: 35,
        labels: {
          align: 'right'
        },
        title: {
          text: ''
        },
        opposite: true,
        tickPositioner() {
          const increment = (balance.max - balance.min) / 4;
          return [balance.min, balance.min + increment, balance.min + increment * 2, balance.min + increment * 3, balance.max];
        },
        ...yAxisExtraConfig
      })
      .setProperty({
        propertyName: 'yAxis.labels.formatter',
        propertyValue(): string {
          const context = this as { value?: number | string };
          return service.shortenWithMagnitude(Number(context.value), 'n2', extraConfig.yAxisUnit);
        },
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'accessibility.enabled',
        propertyValue: true,
        defaultValue: false
      })
      .setProperty({
        propertyName: 'lang.accessibility.chartContainerLabel',
        propertyValue: this.translateService.instant('tdWb.home.balances.balanceTrend.BALANCE_HISTORY'),
        defaultValue: false
      })
      .setProperty({
        propertyName: 'chart',
        propertyValue: {
          marginBottom: 5,
          marginRight: 50,
          marginLeft: 0,
          renderTo: 'td-wb-home-balances-trend-chart-container',
          ...chartExtraConfig
        },
        defaultValue: {}
      });

    return {
      config: config.getConfig(),
      dateRange
    };
  }

  private ceilNumber(value: number): number {
    return this.roundNumber(value, Math.ceil);
  }

  private floorNumber(value: number): number {
    return this.roundNumber(value, Math.floor);
  }

  private roundNumber(value: number, mathMethod: (x: number) => number): number {
    if (value / Math.pow(10, 9) > 1) {
      return mathMethod(value / Math.pow(10, 8)) * Math.pow(10, 8);
    }
    if (value / Math.pow(10, 6) > 1) {
      return mathMethod(value / Math.pow(10, 5)) * Math.pow(10, 5);
    }
    if (value / Math.pow(10, 3) > 1) {
      return mathMethod(value / Math.pow(10, 2)) * Math.pow(10, 2);
    }
    return mathMethod(value * 10) / 10;
  }

  private getBalanceMinAndMax(
    seriesData: DataPoints
  ): { max: number; min: number } {
    let maxLabel;
    let minLabel;

    const max = this.max(
      seriesData,
      (row: DataPoint): number => row[BalanceTrendChartService.VALUE_PROPERTY_NAME]
    );

    const min = this.min(seriesData, (row: DataPoint): number => row[BalanceTrendChartService.VALUE_PROPERTY_NAME]);

    if (max !== undefined) {
      maxLabel = max[BalanceTrendChartService.VALUE_PROPERTY_NAME];
    }
    if (min !== undefined) {
      minLabel = min[BalanceTrendChartService.VALUE_PROPERTY_NAME];
    }

    // We don't want max and min to be the same value
    if (maxLabel === minLabel) {
      minLabel = 0;
    }
    return {
      max: maxLabel,
      min: minLabel
    };
  }

  private getDateRange(
    seriesData: DataPoints
  ): { startDate: Date; endDate: Date } {
    const max = this.max(seriesData, (row: DataPoint): number => row[BalanceTrendChartService.DATE_PROPERTY_NAME]);

    const min = this.min(seriesData, (row: DataPoint): number => row[BalanceTrendChartService.DATE_PROPERTY_NAME]);

    return {
      startDate:
        min !== undefined ? min[BalanceTrendChartService.DATE_PROPERTY_NAME] : undefined,
      endDate:
        max !== undefined ? max[BalanceTrendChartService.DATE_PROPERTY_NAME] : undefined
    };
  }

  private max(collection: DataPoints, callback: (val: DataPoint) => number): DataPoint {
    let max = Number.NEGATIVE_INFINITY;
    let res: DataPoint;
    collection.forEach((val: DataPoint): void => {
      const potentialMax = callback(val);
      if (potentialMax > max) {
        res = val;
        max = potentialMax;
      }
    });
    return res;
  }

  private min(collection: DataPoints, callback: (val: DataPoint) => number): DataPoint {
    let min = Number.POSITIVE_INFINITY;
    let res: DataPoint;
    collection.forEach((val: DataPoint): void => {
      const potentialMin = callback(val);
      if (potentialMin < min) {
        res = val;
        min = potentialMin;
      }
    });
    return res;
  }

  private renderDate(val: Date): string {
    return this.datePipe.transform(val);
  }

  private shortenWithMagnitude(val: number, format: string = 'n3', unit: ChartYAxisUnit = ChartYAxisUnit.NUMBER): string {
    let transformedText: string;

    if (unit === ChartYAxisUnit.CURRENCY) {
      transformedText = this.currencyPipe.transform(val, { format, shorten: true });
    } else {
      transformedText = this.numberPipe.transform(val, { format, shorten: true });
    }

    return transformedText;
  }
}

